import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import ActionPlanEditor, {
  ActionPlanEditorProps,
} from '../../../ActionPlan/ActionPlanEditor';
import {
  ActionPlanSchema,
  ActionPlansSchema,
} from '../../../../definitions/schemas';
import { ActionPlanVisibility } from '../../../../enums/ActionPlans';
import { Events, Properties } from '../../../../enums/Analytics';
import { FullScreenTrackingContext } from '../../../Rooms/Tracking/FullScreenTrackingContext';
import { useI18n } from '../../../../lib/hook/useI18n';
import { UserRolesStore } from '../../../../stores/observables/UserStore';
import Analytics from '../../../../tools/Analytics';

interface ActionPlanProps {
  id: string | undefined;
  visibility: ActionPlanVisibility | undefined;
}

interface IContext {
  openActionPlanEditor: (props?: ActionPlanProps) => void;
}

export const RoomActionPlansContext = React.createContext<IContext>({
  openActionPlanEditor: () => {},
});

type PickedActionPlanEditorProps = Pick<
  ActionPlanEditorProps,
  | 'allowDiscussion'
  | 'allowInvitations'
  | 'allowNameChange'
  | 'collaborators'
  | 'companyName'
  | 'contact'
  | 'deal'
  | 'disableNavigationBlock'
  | 'hideOwnerAndDue'
  | 'displayFeedbackImmediately'
  | 'loadFromReview'
  | 'organizationName'
  | 'referrer'
  | 'roomId'
  | 'roomUUID'
  | 'ShareRoomDialogProps'
  | 'userContactId'
  | 'viewOnly'
>;

type Props = React.PropsWithChildren<
  PickedActionPlanEditorProps & {
    deepLinkActionPlanId?: string;
    deepLinkActionPlanTaskId?: string;
    deepLinkActionPlanTaskMessageId?: string;
    isGuestVisitor?: boolean;
    roomActionPlans: ActionPlansSchema;
    setActionPlans: React.Dispatch<React.SetStateAction<ActionPlansSchema>>;
  }
>;

const RoomActionPlansContextProvider: React.FC<Props> = (
  props: Props
): JSX.Element => {
  const { setFullScreenContentId } = useContext(FullScreenTrackingContext);

  const { t } = useI18n();

  const {
    children,
    contact,
    deal,
    deepLinkActionPlanId,
    deepLinkActionPlanTaskId: initialTaskDeepLinkId = '',
    deepLinkActionPlanTaskMessageId: initialTaskMessageDeepLinkId = '',
    isGuestVisitor = false,
    referrer,
    roomActionPlans,
    setActionPlans,
    ...actionPlanEditorProps
  } = props;

  const [editorOpen, setEditorOpen] = useState(false);

  /**
   * Using a custom state so this can handle the scenario where the user
   * closes de action plan and opens it again, since we don't want to
   * continuously deep link to the task or task message
   */
  const [deepLinkActionPlanTaskId, setDeepLinkActionPlanTaskId] = useState(
    initialTaskDeepLinkId
  );
  const [deepLinkActionPlanTaskMessageId, setDeepLinkActionPlanTaskMessageId] =
    useState(initialTaskMessageDeepLinkId);

  const actionPlanProps = useRef<ActionPlanProps | undefined>();

  /**
   * "Deep link" by opening the action plan editor if the given
   * id matches one of the MAPs associated to the room. When the
   * component is destroyed, resets the deep link id so it's not
   * opened repeated times unnecessarily.
   */
  useEffect(() => {
    const isDeepLinkToCreation = deepLinkActionPlanId === 'new';
    const deepLinkedActionPlan = roomActionPlans.find(
      ({ id }) => id === deepLinkActionPlanId
    );

    if (!(isDeepLinkToCreation || deepLinkedActionPlan)) {
      return;
    }

    openActionPlanEditor({
      id: deepLinkedActionPlan?.id,
      visibility: deepLinkedActionPlan?.visibility,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackActionPlanDrawerOpened = useCallback(() => {
    const currentRole = UserRolesStore.getCurrent();
    if (!currentRole) {
      return;
    }

    Analytics.track(Events.MAP_DRAWER_OPENED, {
      [Properties.ENTERED_FROM]: referrer,
    });
  }, [referrer]);

  /**
   * Removing the deep links, to avoid continuously open the task and
   * conversation message if the user closes and re-opens the editor
   */
  const resetDeepLink = useCallback(() => {
    setDeepLinkActionPlanTaskId('');
    setDeepLinkActionPlanTaskMessageId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Adds, removes or updates action plans for the room based on these scenarios:
   *  - No id, actionPlan present: adds a new action plan
   *  - No actionPlan, id present: removes the action plan with the given id
   *  - id present, actionPlan present: updates the action plan in the state
   */
  const onActionPlanChange = useCallback(
    (id?: string, actionPlan?: ActionPlanSchema) =>
      setActionPlans((previousActionPlans) => {
        // if an action plan is given but no initial ID, it means it's a new one
        if (!id && actionPlan) {
          return [...previousActionPlans, actionPlan];
        }

        // if an ID is given but no action plan, it means it was removed
        if (!actionPlan) {
          return previousActionPlans.filter(
            (prevActionPlan) => prevActionPlan.id !== id
          );
        }

        // if an ID and an action plan is given, it's overwritten
        return previousActionPlans.map((prevActionPlan) =>
          prevActionPlan.id === actionPlan.id ? actionPlan : prevActionPlan
        );
      }),
    [setActionPlans]
  );

  const onClose = useCallback((actionPlan?: ActionPlanSchema) => {
    onActionPlanChange(actionPlanProps.current?.id, actionPlan);

    setEditorOpen(false);
    resetDeepLink();

    /**
     * Removing the MAP id so the pieces of content inside the viewport
     * can restore their tracking, because the action plan editor is now closed
     */
    setFullScreenContentId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ~~~~~~~~~~~~~~ Context methods ~~~~~~~~~~~~~~ //
  const openActionPlanEditor = useCallback(
    (props?: ActionPlanProps) => {
      actionPlanProps.current = props;
      setEditorOpen(true);
      /**
       * Setting a random id to tell every piece of content being tracked to
       * wait until the action plan editor is closed, to avoid tracking
       * time spent in items like documents or images, when they're really
       * are not visible because the action plan is open
       */
      setFullScreenContentId(`action-plan-${props?.id || '0'}`);
      trackActionPlanDrawerOpened();
    },
    [setFullScreenContentId, trackActionPlanDrawerOpened]
  );

  return (
    <RoomActionPlansContext.Provider value={{ openActionPlanEditor }}>
      {children}

      <ActionPlanEditor
        {...actionPlanEditorProps}
        actionPlanId={actionPlanProps.current?.id}
        actionPlanLabel={t('map.label')}
        actionPlanTaskId={deepLinkActionPlanTaskId}
        actionPlanTaskMessageId={deepLinkActionPlanTaskMessageId}
        contact={contact}
        deal={deal}
        isGuestVisitor={isGuestVisitor}
        onClose={onClose}
        open={editorOpen}
        referrer={referrer}
        resetDeepLink={resetDeepLink}
        visibility={actionPlanProps.current?.visibility}
      />
    </RoomActionPlansContext.Provider>
  );
};

export default RoomActionPlansContextProvider;
